import { z } from 'zod';

export enum ProjectAlertStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}
export enum ProjectAlertTriggerType {
  EVENT = 'event',
  USER = 'user',
}

export enum ProjectAlertRecipientType {
  EMAIL = 'email',
  SLACK = 'slack',
  WEBHOOK = 'webhook',
}

export const ProjectAlertTriggerFilter = z.object({
  name: z.string(),
  operator: z.string().optional(),
  value: z.string(),
});

export type ProjectAlertTriggerFilterType = z.infer<
  typeof ProjectAlertTriggerFilter
>;

export const ProjectAlertType = z.object({
  id: z.string(),
  name: z.string(),
  created_at: z.date(),
  updated_at: z.date().nullable(),
  trigger_type: z.nativeEnum(ProjectAlertTriggerType),
  status: z.nativeEnum(ProjectAlertStatus),
  trigger_filters: z.array(ProjectAlertTriggerFilter),
  recipient: z.array(
    z.object({
      type: z.nativeEnum(ProjectAlertRecipientType),
      value: z.array(z.string()),
    }),
  ),
  project_id: z.string(),
});

export type ProjectAlertType = z.infer<typeof ProjectAlertType>;

export const getAlertRequest = z.object({
  params: z.object({
    teamId: z.string(),
    projectId: z.string(),
    alertId: z.string(),
  }),
});
export type GetAlertRequest = z.infer<typeof getAlertRequest>;
export type GetAlertResponse = ProjectAlertType;

export const getAlertsRequest = z.object({
  params: z.object({
    teamId: z.string(),
    projectId: z.string(),
  }),
});
export type GetAlertsRequest = z.infer<typeof getAlertsRequest>;
export type GetAlertsResponse = ProjectAlertType[];

export const updateAlertRequest = z.object({
  params: z.object({
    teamId: z.string(),
    projectId: z.string(),
    alertId: z.string(),
  }),
  body: z.object({
    name: z.string().min(1, 'Name is required'),
    trigger_type: z.nativeEnum(ProjectAlertTriggerType, {
      errorMap: () => ({ message: 'Invalid trigger type' }),
    }),
    trigger_filters: z.array(ProjectAlertTriggerFilter),
    recipient: z
      .array(
        z.object({
          type: z.nativeEnum(ProjectAlertRecipientType),
          value: z.array(z.string()),
        }),
      )
      .optional(),
  }),
});
export type UpdateAlertRequest = z.infer<typeof updateAlertRequest>;
export type UpdateAlertResponse = ProjectAlertType;

export const createAlertRequest = z.object({
  params: z.object({
    teamId: z.string(),
    projectId: z.string(),
  }),
  body: z.object({
    name: z.string().min(1, 'Name is required'),
    trigger_type: z.nativeEnum(ProjectAlertTriggerType, {
      errorMap: () => ({ message: 'Invalid trigger type' }),
    }),
    trigger_filters: z.array(ProjectAlertTriggerFilter),
    recipient: z
      .array(
        z.object({
          type: z.nativeEnum(ProjectAlertRecipientType),
          value: z.array(z.string()),
        }),
      )
      .optional(),
  }),
});

export type CreateAlertRequest = z.infer<typeof createAlertRequest>;
export type CreateAlertResponse = ProjectAlertType;

export const deleteAlertRequest = z.object({
  params: z.object({
    teamId: z.string(),
    projectId: z.string(),
    alertId: z.string(),
  }),
});
export type DeleteAlertRequest = z.infer<typeof deleteAlertRequest>;
export type DeleteAlertResponse = number;

export const toggleAlertStatusRequest = z.object({
  params: z.object({
    teamId: z.string(),
    projectId: z.string(),
    alertId: z.string(),
  }),
  body: z.object({
    status: z.nativeEnum(ProjectAlertStatus, {
      errorMap: () => ({ message: 'Invalid status' }),
    }),
  }),
});
export type ToggleAlertStatusRequest = z.infer<typeof toggleAlertStatusRequest>;
export type ToggleAlertStatusResponse = ProjectAlertType;
